/* eslint-disable max-len */
import React, {
  memo,
  FunctionComponent,
  useRef,
} from "react";

import ScrollBar from "react-scrollbars-custom";
import { mergeRefs } from "react-merge-refs";
import classnames from "classnames";
import { ScrollState } from "react-scrollbars-custom/dist/types/types";

import { CallbackFunctionVariadic } from "#/types/function";

import "./style.scss";

type ReactScrollbarsCustomType = {
  id?: string;
  children: React.ReactNode;
  barWidth?: number;
  styles?: {
    trackXStyle?: React.CSSProperties;
    trackYStyle?: React.CSSProperties;
    thumbXStyle?: React.CSSProperties;
    thumbYStyle?: React.CSSProperties;
  };
  onScrollStop?: CallbackFunctionVariadic<ScrollState> | null;
  noHoverEffect?: boolean;
};

const ReactScrollbarsCustom: FunctionComponent<ReactScrollbarsCustomType> = ({
  id = undefined,
  children,
  barWidth = 12,
  styles,
  onScrollStop,
  noHoverEffect,
  ...rest
}): JSX.Element => {
  const trackXRef = useRef<HTMLDivElement | null>(null);
  const trackYRef = useRef<HTMLDivElement | null>(null);
  const thumbXRef = useRef<HTMLDivElement | null>(null);
  const thumbYRef = useRef<HTMLDivElement | null>(null);
  const hoverEffectClassName = noHoverEffect ? "no-hover-effect" : "";
  const narrowBarHoverClassName = barWidth < 10 ? "narrow-bar" : "";

  return (
    <ScrollBar
      id={id}
      onScrollStart={() => {
        if (trackXRef?.current?.classList?.contains?.("hide")) {
          trackXRef.current.classList.remove("hide");
        }
        if (trackYRef?.current?.classList?.contains?.("hide")) {
          trackYRef.current.classList.remove("hide");
        }
        if (trackXRef?.current?.style?.display !== "none" && trackYRef?.current?.style?.display !== "none") {
          trackYRef.current?.classList.add("has-x-track");
          if (trackYRef?.current) trackYRef.current.style.paddingBottom = barWidth >= 10 ? `${barWidth + 2}px` : `${barWidth}px`;
        } else if (trackYRef.current?.classList.contains("has-x-track")) {
          trackYRef.current?.classList.remove("has-x-track");
          if (trackYRef?.current) trackYRef.current.style.paddingBottom = barWidth >= 10 ? "2px" : "0px";
        }
      }}
      onScrollStop={(e) => {
        if (trackXRef.current && !trackXRef.current.classList.contains("hide")) {
          trackXRef.current.classList.add("hide");
        }
        if (trackYRef.current && !trackYRef.current.classList.contains("hide")) {
          trackYRef.current.classList.add("hide");
        }
        if (onScrollStop) {
          onScrollStop(e);
        }
      }}
      scrollerProps={{
        renderer: ({ elementRef, ...restProps }) => (
          <div
            {...restProps}
            ref={elementRef}
            className="ScrollbarsCustom-Scroller"
            style={{
              ...restProps.style,
            }}
          />
        ),
      }}
      contentProps={{
        renderer: ({ elementRef, ...restProps }) => (
          <div
            {...restProps}
            ref={elementRef}
            className="ScrollbarsCustom-Content"
            style={{
              padding: "1px",
            }}
          />
        ),
      }}
      wrapperProps={{
        renderer: ({ elementRef, ...restProps }) => (
          <div
            {...restProps}
            ref={elementRef}
            className="ScrollbarsCustom-Wrapper"
            style={{
              ...restProps.style,
              right: `${barWidth}px`,
            }}
          />
        ),
      }}
      trackXProps={{
        renderer: ({ elementRef, ...restProps }) => (
          <div
            {...restProps}
            ref={mergeRefs([elementRef, trackXRef])}
            className={classnames(
              "ScrollbarsCustom-Track",
              "ScrollbarsCustom-TrackX",
              "hide",
              hoverEffectClassName,
              narrowBarHoverClassName,
            )}
            style={{
              ...restProps.style,
              bottom: "0px",
              left: "0px",
              width: `calc(100% - ${barWidth}px)`,
              height: `${barWidth}px`,
              background: "",
              paddingLeft: barWidth >= 10 ? "2px" : "0px",
              paddingRight: barWidth >= 10 ? "2px" : "0px",
              borderRadius: barWidth > 10 ? "2px" : "999px",
              ...(styles?.trackXStyle ?? {}),
            }}
          />
        ),
      }}
      trackYProps={{
        renderer: ({ elementRef, ...restProps }) => (
          <div
            {...restProps}
            ref={mergeRefs([elementRef, trackYRef])}
            className={classnames(
              "ScrollbarsCustom-Track",
              "ScrollbarsCustom-TrackY",
              "hide",
              hoverEffectClassName,
              narrowBarHoverClassName,
            )}
            style={{
              ...restProps.style,
              top: "0px",
              width: `${barWidth}px`,
              height: "100%",
              paddingTop: barWidth >= 10 ? "2px" : "0px",
              paddingBottom: barWidth >= 10 ? "2px" : "0px",
              background: "",
              borderRadius: barWidth > 10 ? "2px" : "999px",
              ...(styles?.trackYStyle ?? {}),
            }}
          />
        ),
      }}
      thumbXProps={{
        renderer: ({ elementRef, ...restProps }) => (
          <div
            {...restProps}
            ref={mergeRefs([elementRef, thumbXRef])}
            style={{
              ...restProps.style,
              height: barWidth >= 10 ? `calc(${barWidth}px - 4px)` : "100%",
              marginTop: barWidth >= 10 ? "2px" : "0%",
              background: "",
              borderRadius: "999px",
              ...(styles?.thumbXStyle ?? {}),
            }}
            className="ScrollbarsCustom-Thumb ScrollbarsCustom-ThumbX"
          />
        ),
      }}
      thumbYProps={{
        renderer: ({ elementRef, ...restProps }) => (
          <div
            {...restProps}
            ref={mergeRefs([elementRef, thumbYRef])}
            style={{
              ...restProps.style,
              width: barWidth >= 10 ? `calc(${barWidth}px - 4px)` : "100%",
              marginLeft: barWidth >= 10 ? "2px" : "0%",
              background: "",
              borderRadius: "999px",
              ...(styles?.thumbYStyle ?? {}),
            }}
            className="ScrollbarsCustom-Thumb ScrollbarsCustom-ThumbY"
          />
        ),
      }}
      {...rest}
    >
      {children}
    </ScrollBar>
  );
};

ReactScrollbarsCustom.defaultProps = {
  id: undefined,
  barWidth: 12,
  styles: {
    trackXStyle: {},
    trackYStyle: {},
    thumbXStyle: {},
    thumbYStyle: {},
  },
  onScrollStop: null,
  noHoverEffect: false,
};

export default memo(ReactScrollbarsCustom);
